import { Box, Flex, Text } from '@chakra-ui/react';
import React from 'react';

// Presentational activity list for the Notes & Activity panel — a flat, newest-first feed matching
// the Notes & Activity reference: one row per entry, each with its own avatar, a bold actor leading
// the action text (with outlined chips), a muted timestamp line, and a divider between rows.
//
// This lives INSIDE project_starburst on purpose: the panel's styling must not leak into shared
// components. It is Chakra-only (no shared-views Timeline, no redux) — the out-of-folder adapter
// reduces raw redux activity into the `ActivityFeedItem[]` token model this renders. Chips are a
// styling concern, so the adapter emits `{ chip }` tokens and this component owns their look.

const BORDER = '#ece6dd';
const DIVIDER = '#f4f1ed';
const PRIMARY = '#2a2a2a';
const MUTED = '#9b938a';
const CHIP_BORDER = '#e0d7c9';
const AVATAR_USER_BG = '#fbe9e1';
const AVATAR_SYSTEM_BG = '#fafaf7';

/** A run of entry text: a plain string, or a `{ chip }` to render as an outlined chip. */
export type ActivityToken = string | { readonly chip: React.ReactNode };

export interface ActivityFeedItem {
  /** Stable per-entry key. */
  readonly id: string;
  /** Actor name shown in bold leading the row (e.g. "M. Chen", "System"). */
  readonly actor: string;
  /** Preformatted timestamp label (the adapter decides the exact format). */
  readonly timestamp: string;
  /** The action summary as interleaved text + chip tokens. */
  readonly tokens: readonly ActivityToken[];
  /** Picks the avatar glyph; defaults to 'user'. */
  readonly kind?: 'user' | 'system';
  /** Extra detail lines rendered under the tokens, one `Text` per line. */
  readonly detail?: readonly string[];
}

const Chip: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    as="span"
    display="inline-block"
    verticalAlign="baseline"
    px="6px"
    mx="2px"
    border={`1px solid ${CHIP_BORDER}`}
    borderRadius="4px"
    fontSize="12px"
    fontWeight={600}
    color={PRIMARY}
    lineHeight="1.3"
  >
    {children}
  </Box>
);

const renderTokens = (tokens: readonly ActivityToken[]): React.ReactNode =>
  tokens.map((token, i) =>
    typeof token === 'string' ? (
      <React.Fragment key={i}>{token}</React.Fragment>
    ) : (
      <Chip key={i}>{token.chip}</Chip>
    )
  );

export const ActivityFeed: React.FC<{
  readonly items: readonly ActivityFeedItem[];
}> = ({ items }) => (
  <Box data-testid="activity-feed">
    <Box
      fontSize="11px"
      fontWeight={600}
      letterSpacing="0.03em"
      color={MUTED}
      mb="8px"
    >
      System + user actions
    </Box>

    {items.length === 0 ? (
      <Box data-testid="activity-feed-empty" fontSize="12.5px" color={MUTED}>
        No activity.
      </Box>
    ) : (
      items.map((item, i) => (
        <Flex
          key={item.id}
          data-testid="activity-feed-item"
          gap="11px"
          py="11px"
          borderBottom={
            i < items.length - 1 ? `1px solid ${DIVIDER}` : undefined
          }
          _first={{ pt: 0 }}
        >
          <Box
            aria-hidden
            flexShrink={0}
            display="grid"
            placeItems="center"
            w="26px"
            h="26px"
            borderRadius="7px"
            border={`1px solid ${BORDER}`}
            bg={item.kind === 'system' ? AVATAR_SYSTEM_BG : AVATAR_USER_BG}
            sx={{
              '&::before': {
                content: item.kind === 'system' ? '"⚙"' : '"👤"'
              }
            }}
          />

          <Box flex="1" minW={0}>
            <Box fontSize="13.5px" lineHeight="1.45" color={PRIMARY}>
              <Box as="span" fontWeight={700}>
                {item.actor}
              </Box>{' '}
              {renderTokens(item.tokens)}
            </Box>
            {item.detail?.map((line, i) => (
              <Text key={i} fontSize="12px" color={MUTED} mt="3px">
                {line}
              </Text>
            ))}
            <Box fontSize="12px" color={MUTED} mt="3px">
              {item.timestamp}
            </Box>
          </Box>
        </Flex>
      ))
    )}
  </Box>
);

ActivityFeed.displayName = 'ActivityFeed';
